/**
 * VaultDocumentPanel — right-side panel showing related documents
 * with PDF preview and download capability.
 *
 * Used in PolicyShow and ContentList when the record has attached documents.
 */

import React, { useState, useEffect } from "react";
import { useApiUrl } from "@refinedev/core";
import axios from "axios";
import { useDownloadTracker } from "@/hooks/useDownloadTracker";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { VaultDropZone } from "./VaultDropZone";

interface RelatedDoc {
  id: string;
  notes_title?: string;
  filename?: string;
  filetype?: string;
  filesize?: string;
  createdtime?: string;
  notecontent?: string;
}

interface VaultDocumentPanelProps {
  /** Parent record WSID (e.g. policy or content ID) */
  recordId: string;
  /** Parent module in coreBOS (e.g. "evPolicies", "cbContent") */
  module: string;
  /** Whether the parent record allows uploads (from allow_upload field) */
  allowUpload?: boolean;
}

export const VaultDocumentPanel: React.FC<VaultDocumentPanelProps> = ({
  recordId,
  module,
  allowUpload = false,
}) => {
  const apiUrl = useApiUrl();
  const [docs, setDocs] = useState<RelatedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const { markDownloaded, isDownloaded } = useDownloadTracker();
  const { upload, uploading } = useDocumentUpload();

  // Fetch related documents
  useEffect(() => {
    if (!recordId) return;
    setLoading(true);
    setPreviewUrl(null);

    const fetch = async () => {
      try {
        const res = await axios.post(`${apiUrl}/direct/get-related`, {
          id: recordId,
          module,
          related_module: "Documents",
        });
        if (res.data.success && res.data.result) {
          const raw = res.data.result.records ?? res.data.result;
          const list = Array.isArray(raw) ? raw : Object.values(raw);
          setDocs(list as RelatedDoc[]);
        } else {
          setDocs([]);
        }
      } catch {
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [recordId, module, apiUrl]);

  const handleDownload = async (doc: RelatedDoc) => {
    setDownloading(doc.id);
    try {
      const res = await axios.post(`${apiUrl}/direct/retrieve-attachment`, {
        doc_ids: [doc.id],
        return_file: true,
      });
      if (res.data.success && res.data.result) {
        const fileData = res.data.result[doc.id] || Object.values(res.data.result)[0] as any;
        if (fileData?.filecontent || fileData?.attachment) {
          const base64 = fileData.filecontent || fileData.attachment;
          const mime = fileData.filetype || doc.filetype || "application/octet-stream";
          const link = document.createElement("a");
          link.href = `data:${mime};base64,${base64}`;
          const fname = fileData.filename || doc.filename || doc.notes_title || "download";
          link.download = fname;
          link.click();
          markDownloaded(doc.id, fname);
        }
      }
    } catch {
      // failed
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (doc: RelatedDoc) => {
    // If already previewing this doc, close it
    if (previewName === doc.id) {
      setPreviewUrl(null);
      setPreviewName("");
      return;
    }

    try {
      const res = await axios.post(`${apiUrl}/direct/retrieve-attachment`, {
        doc_ids: [doc.id],
        return_file: true,
      });
      if (res.data.success && res.data.result) {
        const fileData = res.data.result[doc.id] || Object.values(res.data.result)[0] as any;
        if (fileData?.filecontent || fileData?.attachment) {
          const base64 = fileData.filecontent || fileData.attachment;
          const mime = fileData.filetype || doc.filetype || "application/pdf";
          setPreviewUrl(`data:${mime};base64,${base64}`);
          setPreviewName(doc.id);
        }
      }
    } catch {
      // failed
    }
  };

  const isPdf = (doc: RelatedDoc) =>
    (doc.filetype || doc.filename || "").toLowerCase().includes("pdf");

  const isImage = (doc: RelatedDoc) =>
    /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(doc.filename || "") ||
    (doc.filetype || "").startsWith("image/");

  const formatSize = (size?: string) => {
    const n = Number(size);
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div style={{ padding: "var(--vault-spacing-md)" }}>
        <span className="vault-label-tiny">Caricamento allegati...</span>
      </div>
    );
  }

  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      const result = await upload(file, { linkTo: recordId });
      if (result) {
        setDocs((prev) => [...prev, {
          id: result.id,
          notes_title: result.filename,
          filename: result.filename,
          filetype: result.filetype,
          filesize: String(result.filesize),
          createdtime: new Date().toISOString(),
        }]);
      }
    }
  };

  return (
    <div className="vault-zone-download">
      <span className="vault-label-bracketed" style={{ display: "block", marginBottom: "var(--vault-spacing-sm)" }}>
        Allegati ({docs.length})
      </span>

      <div style={{ display: "flex", flexDirection: "column", gap: "1px", background: "var(--vault-grid-line)" }}>
        {docs.map((doc) => (
          <div
            key={doc.id}
            style={{
              background: "var(--vault-bg)",
              padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--vault-spacing-sm)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="vault-text-small" style={{ fontWeight: 500, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {doc.notes_title || doc.filename || "Documento"}
              </span>
              <span className="vault-label-tiny">
                {isDownloaded(doc.id) && (
                  <span
                    title="Scaricato"
                    style={{
                      display: "inline-block",
                      background: "var(--vault-zone-accepted-border)",
                      color: "#fff",
                      fontSize: "0.55rem",
                      padding: "1px 5px",
                      borderRadius: 2,
                      marginRight: 4,
                      fontWeight: 600,
                    }}
                  >
                    ✓ SCARICATO
                  </span>
                )}
                {(doc.filetype || "").replace("application/", "").toUpperCase()}
                {doc.filesize ? ` · ${formatSize(doc.filesize)}` : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: "var(--vault-spacing-xs)", flexShrink: 0 }}>
              {(isPdf(doc) || isImage(doc)) ? (
                <button
                  className="vault-btn-ghost"
                  onClick={() => handlePreview(doc)}
                  style={{ fontSize: "var(--vault-text-tiny)" }}
                  title="Anteprima"
                >
                  {previewName === doc.id ? "Chiudi" : "▷ Anteprima"}
                </button>
              ) : null}
              <button
                className="vault-btn"
                onClick={() => handleDownload(doc)}
                disabled={downloading === doc.id}
                style={{ minWidth: 100, padding: "6px 16px", fontSize: "var(--vault-text-tiny)" }}
              >
                <span>{downloading === doc.id ? "..." : "&#8595; Scarica"}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload zone — only if record allows uploads */}
      {allowUpload && (
        <div style={{ marginTop: "var(--vault-spacing-sm)" }}>
          <VaultDropZone onFiles={handleUpload} disabled={uploading} compact />
        </div>
      )}

      {/* Preview area */}
      {previewUrl && (
        <div style={{
          marginTop: "var(--vault-spacing-sm)",
          border: "1px solid var(--vault-grid-line)",
          background: "#f5f5f5",
        }}>
          {previewUrl.includes("application/pdf") || previewUrl.includes("pdf") ? (
            <iframe
              src={previewUrl}
              style={{ width: "100%", height: 600, border: "none" }}
              title="Anteprima PDF"
            />
          ) : (
            <img
              src={previewUrl}
              alt="Anteprima"
              style={{ width: "100%", maxHeight: 600, objectFit: "contain" }}
            />
          )}
        </div>
      )}
    </div>
  );
};
